// Considered: SearchFacetsMolecule — rejected because it renders count-driven ES aggregation
// buckets (`FacetBucket[]`), and `GET /composables/search` returns no facet aggregations (see
// composablesSearch.ts). The kind toggle is a fixed-enum single-select strip, which is the
// canonical-atoms-table row for SegmentedControlAtom (mode="single", variant="pills"), so it
// composes that rather than pressed TagAtoms — ScopeFilterMolecule's hand-rolled pill strip is
// the pattern this deliberately does NOT copy (atomic-design.md Rule 3).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { PLACEABLE_KINDS_BY_TARGET, isKindPlaceableForTarget, type ComposerTargetKind } from '@center-platform/component-catalog';
import {
  searchComposables,
  type ComposablesSearchResult,
  type ComposablesSearchScope,
} from '../../../api/marketplaceApi';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import './RegistryRailOrganism.scss';

export type { ComposerTargetKind };
type KindOption = (typeof PLACEABLE_KINDS_BY_TARGET)['space'][number] | (typeof PLACEABLE_KINDS_BY_TARGET)['composition'][number];

export interface RegistryRailOrganismProps {
  /** `search-service`'s client — `/composables/search` is not on `schemaClient`. */
  apiClient?: ApiClient;
  /** Restricts results to an approach's dependency closure (SC7) — a hint; the server re-derives
   * and enforces it from the token. Absent renders unscoped (org-wide). */
  scope?: ComposablesSearchScope;
  surface?: 'a2ui' | 'metaspace' | 'space';
  /**
   * What kind of thing the caller is composing (EAINT-844 Phase F4 "Grammar") — constrains both
   * the kind toggle shown and the results rendered to what may legally be PLACED into that
   * target. Defaults to `'space'` (the only live composer target today — OQ-8).
   */
  targetKind?: ComposerTargetKind;
  /** Fires when the caller places a result into the draft (the rail's "+ Add" affordance). Absent hides Add (read-only/locked targets). */
  onAdd?: (result: ComposablesSearchResult) => void;
  /** Fires for a composition-backed ("template"-origin) result's "open in playground" action.
   * Named for where it actually lands — the read-only Explorer playground. A composition-target
   * composer shell does not exist yet (spec OQ-8); when it does, this gains a sibling. */
  onOpenInPlayground?: (systemName: string) => void;
  /** Fires for a composition-backed result's "fork" action. Absent hides the action. */
  onFork?: (systemName: string) => void;
  className?: string;
}

const DEBOUNCE_MS = 300;

/**
 * The composer shell's registry rail (SC —  spec UI table): a scoped `search_composables`
 * browser sitting beside the canvas, distinct from the Explorer's full tree/story/docs
 * workspace. Search + kind toggle are client state; the server is the authority on scope
 * (`scope` here is a hint per space-composer.md's "Scope model").
 */
/** How the single-select strip spells "no kind filter"; the state itself stays `null`. */
const ALL_KINDS = 'all';

const RegistryRailOrganism: React.FC<RegistryRailOrganismProps> = ({
  apiClient,
  scope,
  surface,
  targetKind = 'space',
  onAdd,
  onOpenInPlayground,
  onFork,
  className = '',
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [kindFilter, setKindFilter] = useState<KindOption | null>(null);
  const [results, setResults] = useState<ComposablesSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const [error, setError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // What may legally be PLACED into the current target (EAINT-844 Phase F4 "Grammar") — a Space
  // never offers primitives or a second surface; a composition never offers sections or a
  // surface. Resets any filter the caller had picked under a previous targetKind.
  const kindOptions = PLACEABLE_KINDS_BY_TARGET[targetKind];
  useEffect(() => {
    setKindFilter((prev) => (prev && kindOptions.includes(prev) ? prev : null));
  }, [kindOptions]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedTerm(searchTerm.trim()), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  const scopeKey = scope?.kind === 'approach' ? `approach:${scope.systemName}` : scope?.kind ?? '';

  useEffect(() => {
    if (!apiClient) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    searchComposables(apiClient, {
      q: debouncedTerm || undefined,
      kind: kindFilter ?? undefined,
      surface,
      scope,
    })
      .then((response) => {
        if (cancelled) return;
        // Client-side grammar filter (belt): an unfiltered ("All") query returns every kind the
        // surface serves, including ones this target may never place (e.g. a Space's own
        // primitives/surfaces). A single-kind query is already constrained by `kindOptions`
        // above, so this is a no-op in that case.
        setResults(response.results.filter((result) => isKindPlaceableForTarget(result.kind, targetKind)));
        setDegraded(response.degraded);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, debouncedTerm, kindFilter, surface, scopeKey, targetKind]);

  const isEmpty = !loading && !error && results.length === 0;

  const originLabelKey = useMemo(
    () => (origin: ComposablesSearchResult['origin']) =>
      origin === 'catalog' ? 'composer.explorer.backingCatalog' : 'composer.explorer.backingComposition',
    []
  );

  return (
    <div className={['registry-rail', className].filter(Boolean).join(' ')} data-testid="registry-rail">
      <div className="registry-rail__header">
        <h3 className="registry-rail__title">{t('composer.rail.title')}</h3>
        <InputAtom
          className="registry-rail__search"
          value={searchTerm}
          placeholder={t('composer.rail.searchPlaceholder')}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label={t('composer.rail.searchPlaceholder')}
          data-testid="registry-rail-search"
        />
        {/* `all` is the strip's own clear affordance, so the filter is cleared by selecting it
            rather than by re-clicking the active kind — the idiom of a single-select control. */}
        <SegmentedControlAtom
          className="registry-rail__kind-toggle"
          mode="single"
          variant="pills"
          size="small"
          renderAs="button"
          ariaLabel={t('composer.rail.kindFilterLabel')}
          value={kindFilter ?? ALL_KINDS}
          onChange={(value) => setKindFilter(value === ALL_KINDS ? null : (value as KindOption))}
          options={[
            { value: ALL_KINDS, label: t('composer.rail.kindAll'), testId: 'registry-rail-kind-all' },
            ...kindOptions.map((kind) => ({
              value: kind,
              label: t(`composer.kind.${kind}`),
              testId: `registry-rail-kind-${kind}`,
            })),
          ]}
          data-testid="registry-rail-kind-toggle"
        />
      </div>

      {degraded && (
        <div data-testid="registry-rail-degraded">
          <MessageAtom severity="warn" text={t('composer.rail.degraded')} />
        </div>
      )}
      {error && <MessageAtom severity="error" text={t('composer.rail.loadFailed')} />}

      <ul className="registry-rail__list" data-testid="registry-rail-list">
        {loading && <li className="registry-rail__status">{t('composer.rail.loading')}</li>}
        {isEmpty && (
          <li className="registry-rail__status">
            {debouncedTerm || kindFilter ? t('composer.rail.noResults') : t('composer.rail.empty')}
          </li>
        )}
        {!loading &&
          results.map((result) => (
            <li key={`${result.origin}:${result.systemName}`} className="registry-rail__item" data-testid={`registry-rail-item-${result.systemName}`}>
              <div className="registry-rail__item-header">
                <span className="registry-rail__item-name">{result.name}</span>
                {result.kind && <ChipAtom className="registry-rail__item-kind" label={t(`composer.kind.${result.kind}`)} />}
                {onAdd && (
                  <ButtonAtom
                    variant="text"
                    size="small"
                    icon="pi pi-plus"
                    aria-label={t('composer.rail.addAriaLabel', { name: result.name })}
                    data-testid={`registry-rail-add-${result.systemName}`}
                    onClick={() => onAdd(result)}
                  >
                    {t('composer.rail.add')}
                  </ButtonAtom>
                )}
              </div>
              <div className="registry-rail__item-badges">
                <span className="registry-rail__item-badge">{t(originLabelKey(result.origin))}</span>
                {result.tier === 'organization' && (
                  <span className="registry-rail__item-badge">{t('composer.rail.tierOrganization')}</span>
                )}
              </div>
              {result.description && <p className="registry-rail__item-description">{result.description}</p>}
              {result.origin === 'template' && (onOpenInPlayground || onFork) && (
                <div className="registry-rail__item-actions">
                  {onOpenInPlayground && (
                    <ButtonAtom variant="text" size="small" onClick={() => onOpenInPlayground(result.systemName)}>
                      {t('composer.explorer.openInPlayground')}
                    </ButtonAtom>
                  )}
                  {onFork && (
                    <ButtonAtom variant="text" size="small" onClick={() => onFork(result.systemName)}>
                      {t('composer.explorer.fork')}
                    </ButtonAtom>
                  )}
                </div>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
};

export default RegistryRailOrganism;
